<template>
  <div class="field-wrapper-wuk sword-date-wuk" :class="{'--bg-fill': fillBg }">
    <slot name="label">
      <input-label v-if="label"
                   :label="label"
                   :style-type="labelStyle"
                   :info-text="labelInfo"
                   :required="required && showRequiredStar"
                   :disabled="disabled">
      </input-label>
    </slot>
    <div class="formatted-date-field-wuk sword-boxed-input-wuk"
         :class="[inputStateClass, errorFeedbackClass, disabled && 'disabled']">
      <!-- First cell -->
      <base-input type="number"
                  class="date-field-input"
                  :name="`${name}-0`"
                  :id="uniqueId"
                  :value="formattedInput[0].model"
                  @input="updateCell($event, formattedInput[0].modelName)"
                  :placeholder="formattedInput[0].placeholder"
                  :required="required"
                  :disabled="disabled"
                  @focus="setAsFocus($event, formattedInput[0].name)"
                  @blur="removeAsFocus" />
      <span class="date-field-divider fc-blue-4">/</span>
      <!-- Second cell -->
      <base-input type="number"
                  class="date-field-input"
                  :name="`${name}-1`"
                  :id="uniqueId"
                  :value="formattedInput[1].model"
                  @input="updateCell($event, formattedInput[1].modelName)"
                  :placeholder="formattedInput[1].placeholder"
                  :required="required"
                  :disabled="disabled"
                  @focus="setAsFocus($event, formattedInput[1].name)"
                  @blur="removeAsFocus" />
      <span class="date-field-divider fc-blue-4">/</span>
      <!-- Third cell -->
      <base-input type="number"
                  class="date-field-input"
                  :name="`${name}-2`"
                  :id="uniqueId"
                  :value="formattedInput[2].model"
                  @input="updateCell($event, formattedInput[2].modelName)"
                  :placeholder="formattedInput[2].placeholder"
                  :required="required"
                  :disabled="disabled"
                  @focus="setAsFocus($event, formattedInput[2].name)"
                  @blur="removeAsFocus" />
    </div>
    <input-footer-wuk
      v-if="!hideErrors"
      :is-invalid="invalidField"
      :validator="validation"
      :manual-error="manualError"
      :help-text="helpText"
      :success-text="successText"
    />
  </div>
</template>

<script>
import inputHandler from '@ui-kit/mixins/inputHandler';
import inputValidationTriggers from '@ui-kit/mixins/inputValidationTriggers';
import BaseInput from '@ui-kit/components/inputs/BaseInput.vue';
import InputLabel from '@ui-kit/components/inputs/labels/InputLabel.vue';
import InputFooterWuk from '@ui-kit/components/inputs/footnotes/InputFooter.vue';

import validationClasses from '@ui-kit/mixins/inputValidationClasses';

export default {
  name: 'SwordFormattedDateWuk',
  mixins: [inputHandler, inputValidationTriggers, validationClasses],
  components: {
    InputLabel,
    InputFooterWuk,
    BaseInput,
  },
  props: {
    value: {
      type: String,
      required: true,
    },
    inputFormat: {
      type: String,
      default: 'mm-dd-yyyy',
    },
    name: {
      type: String,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    let date = new Date(this.value);
    if (this.value?.split('-').length === 3) {
      const [year, month, day] = this.value?.split('-') || [];
      date = new Date();
      date.setDate(day);
      date.setFullYear(year);
      date.setMonth(month - 1);
    }

    return {
      numberRegex: /[0-9]/,
      day: `${this.value ? date.getDate() : ''}`,
      month: `${this.value ? date.getMonth() + 1 : ''}`,
      year: `${this.value ? date.getFullYear() : ''}`,
    };
  },
  computed: {
    dateInputs() {
      return [
        {
          modelName: 'month',
          placeholder: 'MM',
          model: this.month,
        }, {
          modelName: 'day',
          placeholder: 'DD',
          model: this.day,
        }, {
          modelName: 'year',
          placeholder: 'YYYY',
          model: this.year,
        },
      ];
    },
    formattedInput() {
      const formatArray = this.inputFormat.split('-')
                              .map(e => e.toUpperCase());
      // eslint-disable-next-line arrow-body-style
      const sortingFunction = (a, b) => {
        return formatArray.indexOf(a.placeholder) - formatArray.indexOf(b.placeholder);
      };
      return [...this.dateInputs].sort(sortingFunction);
    },
  },
  methods: {
    updateCell(event, model) {
      this[model] = event;
      this.updateOutput();
    },
    notNumber(val) {
      return !this.numberRegex.test(val) && val !== '';
    },
    updateOutput() {
      if (!this.year || !this.month || !this.day) {
        return this.$emit('input', '');
      }

      // return value formatted as YYYY-MM-DD
      const dateString = `${this.year.padStart(4, '0')}-${this.month.padStart(2, '0')}-${this.day.padStart(2, '0')}`;
      return this.$emit('input', dateString);
    },
  },
  watch: {
    day(nVal, oVal) {
      if ((nVal && (nVal < 1 || nVal > 31)) || this.notNumber(nVal)) {
        this.updateCell(oVal, 'day');
      }
    },
    month(nVal, oVal) {
      if ((nVal && (nVal < 1 || nVal > 12)) || this.notNumber(nVal)) {
        this.updateCell(oVal, 'month');
      }
    },
    year(nVal, oVal) {
      if ((nVal && (Number(nVal) < 0 || nVal.length > 4)) || this.notNumber(nVal)) {
        this.updateCell(oVal, 'year');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .formatted-date-field-wuk {
    position: relative;
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;

    .sword-base-input-wuk.date-field-input {
      flex-basis: 33%;
      flex-grow: 1;
      justify-content: flex-end;
      min-width: 0; // Solves issue with Firefox not respecting flex-shrink
      padding-right: 0.375em;
      padding-left: 0.375em;
      text-align: center;
    }

    &-divider {
      pointer-events: none;
    }
  }

  .formatted-date-field[class*='invalid-'] input:not(:focus),
  input[class*='invalid-']:not(:focus) {
    color: $input-error-color;
  }

  .formatted-date-field[class*='invalid-'] {
    border-color: $input-error-color;
  }
</style>
